"use client"

import type React from "react"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Switch } from "@/components/ui/switch"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Edit, Trash2, Plus, Search, Mail, Phone, Globe, Eye, EyeOff, ShieldOff, UserCog, Send, Copy, Check, Loader2, Crown, MoreVertical, Shield } from "lucide-react"
import { type User, useUsersQuery } from "@/hooks/queries/users"
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useUpdateUserPermissionsMutation,
} from "@/hooks/mutations/users"
import { UserPermissionsDialog } from "./user-permissions-dialog"
import { useSession } from "next-auth/react"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { ROLE_HIERARCHY } from "@/lib/auth/permissions.utils"
import { USERS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { canTransferOwnership } from "@/lib/tenants/ownership"

/** A pending invite whose expiry is in the past — no longer a valid "INVITED" state. */
function isInviteExpired(user: { invite_token?: string | null; invite_expires?: string | null }): boolean {
  return (
    !!user.invite_token &&
    !!user.invite_expires &&
    new Date(user.invite_expires).getTime() < Date.now()
  )
}

export function UserManagementPage({ onInvite }: { onInvite?: () => void }) {
  const usersQuery = useUsersQuery()
  const createUserMutation = useCreateUserMutation()
  const updateUserMutation = useUpdateUserMutation()
  const deleteUserMutation = useDeleteUserMutation()
  const updatePermissionsMutation = useUpdateUserPermissionsMutation()
  const users = usersQuery.data
  const isLoading = usersQuery.isLoading
  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedRole, setSelectedRole] = useState<string>("all")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)
  const [isResendingInvite, setIsResendingInvite] = useState(false)
  const [hasSendingEmail, setHasSendingEmail] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [emailSendStatus, setEmailSendStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<{ uuid: string; name: string } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  // Edit-dialog-scoped error so validation/server messages render INSIDE the
  // dialog instead of on the page behind it.
  const [editError, setEditError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    role: "viewer" as User['role'],
    name: "",
    phone: "",
    website: "",
    description: "",
    active: true,
  })

  const currentUser = useSession().data?.user ?? null

  const isSuperUser = currentUser && currentUser.role === "super_user"
  // Permission-based access checks
  const canViewUsers = can(USERS_PERMISSIONS.VIEW)
  const canCreateUsers = can(USERS_PERMISSIONS.CREATE)
  const canEditUsers = can(USERS_PERMISSIONS.EDIT)
  const canDeleteUsers = can(USERS_PERMISSIONS.DELETE)

  // Active tenant — for super-user this honours impersonation, matching the
  // semantics of the rest of this page.
  const activeTenantId = currentUser?.impersonated_tenant_id || currentUser?.tenant_id || null

  // Current owner of the active tenant. Loaded via GET /api/admin/tenants/:id/owner.
  // Kept in component state so the OWNER badge updates live after a successful
  // Make-Owner action without a full page refresh.
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null)
  const [makeOwnerTarget, setMakeOwnerTarget] = useState<{ uuid: string; name: string } | null>(null)
  const [isMakingOwner, setIsMakingOwner] = useState(false)
  // Target user for the permissions dialog (opened from a card's actions menu).
  const [permissionsTarget, setPermissionsTarget] = useState<User | null>(null)

  // Ownership transfer is a DOMAIN rule, not a permission (see
  // lib/tenants/ownership.ts): only the current owner (any role) or a
  // super_user may reassign. Deliberately NOT gated on a `can()` permission.
  const canManageOwner = !!currentUser && canTransferOwnership(currentUser, { owner_user_id: ownerUserId })

  useEffect(() => {
    if (!activeTenantId || !canViewUsers) return
    let cancelled = false
    void (async () => {
      try {
        const res = await fetchWithAuth(`/api/admin/tenants/${activeTenantId}/owner`)
        if (!res.ok) return
        const json = (await res.json()) as { tenant?: { owner_user_id?: string | null } }
        if (!cancelled) setOwnerUserId(json.tenant?.owner_user_id ?? null)
      } catch {
        // Silent — the OWNER badge simply won't show. The page is still usable.
      }
    })()
    return () => { cancelled = true }
  }, [activeTenantId, canViewUsers, fetchWithAuth])

  const handleConfirmMakeOwner = async () => {
    if (!makeOwnerTarget || !activeTenantId) return
    setIsMakingOwner(true)
    try {
      const res = await fetchWithAuth(`/api/admin/tenants/${activeTenantId}/owner`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ user_uuid: makeOwnerTarget.uuid }),
      })
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string }
        setStatus({ type: "error", message: err.error || "Failed to set owner" })
      } else {
        setOwnerUserId(makeOwnerTarget.uuid)
        setStatus({ type: "success", message: `${makeOwnerTarget.name} is now the tenant owner` })
      }
    } catch {
      setStatus({ type: "error", message: "Failed to set owner" })
    } finally {
      setIsMakingOwner(false)
      setMakeOwnerTarget(null)
      setTimeout(() => setStatus(null), 3000)
    }
  }

  const handleUpdatePermissions = async (
    uuid: string,
    additionalPermissions: string[],
    disabledPermissions: string[],
  ) => {
    try {
      await updatePermissionsMutation.mutateAsync({ uuid, additionalPermissions, disabledPermissions })
      setStatus({ type: "success", message: "Permissions updated successfully" })
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update permissions"
      setStatus({ type: "error", message })
      // Surface the failure to the dialog so it stays open with the edits intact
      // for a retry, rather than closing as if the save had succeeded.
      throw error
    } finally {
      setTimeout(() => setStatus(null), 3000)
    }
  }

  // Use shared ROLE_HIERARCHY constant (lower number = higher privilege)
  const getRoleLevel = (role: string): number => ROLE_HIERARCHY[role as keyof typeof ROLE_HIERARCHY] ?? 99
  const currentUserLevel = currentUser ? getRoleLevel(currentUser.role) : 99

  // Check if current user can edit/delete target user (must have higher privilege = lower level number)
  const canManageUser = (targetUser: { role: string; uuid?: string }): boolean => {
    if (!currentUser) return false
    // Can always edit yourself
    if (targetUser.uuid === currentUser.user_id) return true
    // Can only manage users with lower privilege (higher level number)
    const targetLevel = getRoleLevel(targetUser.role)
    return currentUserLevel < targetLevel
  }

  // Check if current user can manage target user's PERMISSIONS
  // Can manage own permissions (to disable/remove) or users with strictly lower role
  const canManageUserPermissions = (targetUser: { role: string; uuid?: string }): boolean => {
    if (!currentUser) return false
    // Can always manage own permissions (to disable role perms or remove granted ones)
    if (targetUser.uuid === currentUser.user_id) return true
    // Can manage users with strictly lower privilege (higher level number)
    const targetLevel = getRoleLevel(targetUser.role)
    return currentUserLevel < targetLevel
  }

  // Check if super_user has selected a tenant
  const hasActiveTenant = currentUser && (currentUser.impersonated_tenant_id || currentUser.tenant_id)

  // Wait for current user to load before checking permissions
  if (!currentUser) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
        <div className="text-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    )
  }

  // Redirect if user doesn't have permission to view users
  if (!canViewUsers) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to access user management.</p>
        </div>
      </div>
    )
  }

  // Show message if super_user hasn't selected a tenant
  if (isSuperUser && !hasActiveTenant) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCog className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Tenant</h3>
          <p className="text-gray-600 mb-4">
            Please select a tenant from the dropdown in the top-right corner to view and manage users.
          </p>
          <p className="text-sm text-gray-500">
            As a Super User, you can switch between tenants to manage their users.
          </p>
        </div>
      </div>
    )
  }

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone && user.phone.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesRole = selectedRole === "all" || user.role === selectedRole

    return matchesSearch && matchesRole
  })

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      role: "viewer",
      name: "",
      phone: "",
      website: "",
      description: "",
      active: true,
    })
    setEditingUser(null)
    setShowPassword(false)
    setShowConfirmPassword(false)
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError(null)

    if (formData.password !== formData.confirmPassword) {
      setCreateError("Passwords do not match")
      return
    }

    if (formData.password.length < 6) {
      setCreateError("Password must be at least 6 characters long")
      return
    }

    try {
      await createUserMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
        role: formData.role,
        name: formData.name,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        description: formData.description || undefined,
        timezone: "UTC",
        uses_dst: false,
      })

      setStatus({ type: "success", message: "User created successfully" })
      setIsCreateDialogOpen(false)
      resetForm()
    } catch (error: unknown) {
      setCreateError(error instanceof Error ? error.message : "Failed to create user")
    }
  }

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingUser) return
    setEditError(null)

    if (formData.password && formData.password !== formData.confirmPassword) {
      setEditError("Passwords do not match")
      return
    }

    if (formData.password && formData.password.length > 0 && formData.password.length < 6) {
      setEditError("Password must be at least 6 characters long")
      return
    }

    try {
      const updateData: Partial<User> & { password?: string } = {
        email: formData.email,
        role: formData.role as User['role'],
        name: formData.name,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        description: formData.description || undefined,
        active: formData.active,
      }

      if (formData.password) {
        updateData.password = formData.password
      }

      await updateUserMutation.mutateAsync({ uuid: editingUser.uuid, userData: updateData })

      setStatus({ type: "success", message: "User updated successfully" })
      setIsEditDialogOpen(false)
      resetForm()
    } catch (error: unknown) {
      setEditError(error instanceof Error ? error.message : "Failed to update user")
    }
  }

  const openDeleteDialog = (userUuid: string, userName: string) => {
    setUserToDelete({ uuid: userUuid, name: userName })
    setDeleteDialogOpen(true)
  }

  const handleDeleteUser = async () => {
    if (!userToDelete) return

    setIsDeleting(true)
    try {
      await deleteUserMutation.mutateAsync(userToDelete.uuid)
      setStatus({ type: "success", message: `User "${userToDelete.name}" deleted successfully` })
      setTimeout(() => setStatus(null), 3000)
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    } catch (error: unknown) {
      setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to delete user" })
      setTimeout(() => setStatus(null), 3000)
    } finally {
      setIsDeleting(false)
    }
  }

  const checkSendingEmail = async () => {
    try {
      const response = await fetchWithAuth("/api/auth/google/tokens", {
        method: "POST",
        headers: {
        },
      })

      if (response.ok) {
        const data = await response.json()
        const hasSending = data.tokens.some((t: { use_for_sending?: boolean }) => t.use_for_sending === true)
        setHasSendingEmail(hasSending)
      } else {
        console.error("Failed to check sending email:", response.status, response.statusText)
        setHasSendingEmail(false)
      }
    } catch (error) {
      console.error("Error checking sending email:", error)
      setHasSendingEmail(false)
    }
  }

  const openEditDialog = async (user: User) => {
    setEditingUser(user)
    setFormData({
      email: user.email,
      password: "",
      confirmPassword: "",
      role: user.role,
      name: user.name,
      phone: user.phone || "",
      website: user.website || "",
      description: user.description || "",
      active: user.active,
    })
    setIsCopied(false)
    setEmailSendStatus(null)
    setEditError(null)

    // Check if sending email is configured when opening dialog for invited user
    if (user.invite_token && !user.active) {
      await checkSendingEmail()
    }

    setIsEditDialogOpen(true)
  }

  const copyInviteLink = async () => {
    if (!editingUser || !editingUser.invite_token) return

    const inviteUrl = `${window.location.origin}/auth/set-password?token=${editingUser.invite_token}`

    try {
      await navigator.clipboard.writeText(inviteUrl)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error("Error copying to clipboard:", error)
      setStatus({ type: "error", message: "Failed to copy link to clipboard" })
      setTimeout(() => setStatus(null), 3000)
    }
  }

  const handleResendInvite = async () => {
    if (!editingUser) return

    setIsResendingInvite(true)
    setEmailSendStatus(null)

    try {
      const response = await fetchWithAuth(`/api/users/${editingUser.uuid}/resend-invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      })

      const data = await response.json()

      if (response.ok) {
        if (data.email_sent) {
          setEmailSendStatus({
            type: "success",
            message: `✅ Invitation email sent successfully to ${editingUser.email}`
          })
          setStatus({ type: "success", message: "Invitation email sent successfully" })
        } else {
          setEmailSendStatus({
            type: "error",
            message: "⚠️ Email was not sent. Please check your email configuration."
          })
          setStatus({ type: "error", message: "Failed to send invitation email" })
        }
        setTimeout(() => {
          setStatus(null)
        }, 3000)
      } else {
        setEmailSendStatus({
          type: "error",
          message: `❌ ${data.error || "Failed to resend invitation"}`
        })
        setStatus({ type: "error", message: data.error || "Failed to resend invitation" })
        setTimeout(() => {
          setStatus(null)
        }, 3000)
      }
    } catch (error: unknown) {
      console.error("Error resending invitation:", error)
      setEmailSendStatus({
        type: "error",
        message: "❌ Network error. Please try again."
      })
      setStatus({ type: "error", message: "Failed to resend invitation email" })
      setTimeout(() => {
        setStatus(null)
      }, 3000)
    } finally {
      setIsResendingInvite(false)
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "super_user":
        return "bg-purple-100 text-purple-800"
      case "integrator":
        return "bg-indigo-100 text-indigo-800"
      case "admin":
        return "bg-red-100 text-red-800"
      case "manager":
        return "bg-blue-100 text-blue-800"
      case "viewer":
        return "bg-cyan-100 text-cyan-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return "N/A"
    }
    try {
      const date = new Date(dateString)
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid date"
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch (error) {
      console.error("Error formatting date:", error, dateString)
      return "Invalid date"
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-gray-500">Loading users...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">User Management</h1>
          <p className="text-gray-600 mt-1">Manage system users and their permissions</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-center">
          {onInvite ? (
            <Button onClick={onInvite} className="w-full sm:w-auto">
              <Send className="mr-2 h-4 w-4" />
              <span>Invite User</span>
            </Button>
          ) : null}
          {canCreateUsers ? (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm()
                  setCreateError(null)
                  setIsCreateDialogOpen(true)
                }}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span>Add New User</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4" autoComplete="off">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value: string) => setFormData({ ...formData, role: value as User['role'] })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {isSuperUser && <SelectItem value="super_user">Super User</SelectItem>}
                      {/* integrator is platform-staff: only super_user can assign it (D1) */}
                      {isSuperUser && <SelectItem value="integrator">Integrator</SelectItem>}
                      {currentUserLevel <= ROLE_HIERARCHY.admin && <SelectItem value="admin">Admin</SelectItem>}
                      {currentUserLevel <= ROLE_HIERARCHY.manager && <SelectItem value="manager">Manager</SelectItem>}
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                  autoComplete="off"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              {createError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">
                    {createError}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
            </Dialog>
          ) : null}
        </div>
      </div>

      {status && (
        <Alert className={status.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
          <AlertDescription className={status.type === "success" ? "text-green-800" : "text-red-800"}>
            {status.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Search and Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="relative w-full flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search users by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_user">Super User</SelectItem>
              <SelectItem value="integrator">Integrator</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-gray-500 sm:whitespace-nowrap">
            {filteredUsers.length} <span>of</span> {users.length} <span>users</span>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Edit className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {users.length === 0 ? "No users found" : "No users match your search"}
          </h3>
          <p className="text-gray-600 mb-6">
            {users.length === 0 ? "Start by creating your first user." : "Try adjusting your search terms or filters."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => {
            // Per-user action gates — used both to render the kebab menu items and
            // to decide whether to show the menu at all vs. a "Higher role" badge.
            const showPermissions = can(USERS_PERMISSIONS.MANAGE_PERMISSIONS) && canManageUserPermissions(user)
            const showMakeOwner =
              canManageOwner && ownerUserId !== user.uuid && user.active && !user.invite_token
            const manageable = canManageUser(user)
            const showEdit = manageable && canEditUsers
            const showDelete = manageable && canDeleteUsers
            const hasActions = showPermissions || showMakeOwner || showEdit || showDelete
            return (
            <Card key={user.uuid} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-medium text-gray-900 mb-1 truncate" title={user.name}>{user.name}</CardTitle>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={getRoleBadgeColor(user.role)}>{user.role.toUpperCase()}</Badge>
                      {user.invite_token && !isInviteExpired(user) ? (
                        <Badge className="bg-yellow-100 text-yellow-800">INVITED</Badge>
                      ) : (
                        <Badge className={user.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                          {user.active ? "ACTIVE" : isInviteExpired(user) ? "INVITE EXPIRED" : "INACTIVE"}
                        </Badge>
                      )}
                      {ownerUserId === user.uuid && (
                        <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          <span>OWNER</span>
                        </Badge>
                      )}
                    </div>
                  </div>
                  {hasActions ? (
                    // modal={false}: a modal DropdownMenu sets `pointer-events: none`
                    // on <body> while closing. Selecting an item opens a Dialog/AlertDialog
                    // in the same tick, which snapshots that value and restores it on close —
                    // leaving the whole page frozen. Dropping the menu's scroll-lock avoids it.
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="shrink-0 px-2" title="User actions">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {showPermissions && (
                          <DropdownMenuItem onClick={() => setPermissionsTarget(user)}>
                            <Shield className="w-4 h-4 mr-2" />
                            <span>Manage permissions</span>
                          </DropdownMenuItem>
                        )}
                        {/*
                          Make-Owner is a DOMAIN action (current owner / super_user),
                          independent of the role-hierarchy `canManageUser` gate — so
                          ownership can be handed to ANY active member, including a
                          higher-role user or another super_user. Hidden for invited,
                          inactive, and the current owner.
                        */}
                        {showMakeOwner && (
                          <DropdownMenuItem
                            onClick={() => setMakeOwnerTarget({ uuid: user.uuid, name: user.name })}
                            className="text-amber-600 focus:text-amber-700"
                          >
                            <Crown className="w-4 h-4 mr-2" />
                            <span>Make owner</span>
                          </DropdownMenuItem>
                        )}
                        {showEdit && (
                          <DropdownMenuItem onClick={() => openEditDialog(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            <span>Edit</span>
                          </DropdownMenuItem>
                        )}
                        {showDelete && (
                          <DropdownMenuItem
                            onClick={() => openDeleteDialog(user.uuid, user.name)}
                            className="text-red-600 focus:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            <span>Delete</span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : !manageable ? (
                    // Role hierarchy is the actual blocker (target outranks the current
                    // user). When the user IS manageable but simply lacks action grants,
                    // render nothing rather than a misleading "Higher role" badge.
                    <Badge variant="outline" className="text-xs text-gray-500 shrink-0">
                      Higher role
                    </Badge>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 truncate">{user.email}</span>
                    </div>
                    {user.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{user.phone}</span>
                      </div>
                    )}
                    {user.website && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <a
                          href={user.website.startsWith("http") ? user.website : `https://${user.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 truncate"
                        >
                          {user.website}
                        </a>
                      </div>
                    )}
                  </div>

                  {user.description && <div className="text-sm text-gray-600 line-clamp-2">{user.description}</div>}

                  {user.invite_token && user.invite_expires && !isInviteExpired(user) && (
                    <div className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded">
                      <span>Invitation expires:</span> {formatDate(user.invite_expires)}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 pt-2 border-t">
                    <div><span>Created:</span> {formatDate(user.created_at)}</div>
                    {user.updated_at !== user.created_at && <div><span>Updated:</span> {formatDate(user.updated_at)}</div>}
                  </div>
                </div>
              </CardContent>
            </Card>
            )
          })}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) setEditError(null) }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="edit-name">Full Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
                <div className="relative">
                  <Input
                    id="edit-password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="edit-confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="edit-confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="edit-role">Role *</Label>
                <Select value={formData.role} onValueChange={(value: string) => setFormData({ ...formData, role: value as User['role'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperUser && <SelectItem value="super_user">Super User</SelectItem>}
                    {/* integrator is platform-staff: only super_user can assign it (D1) */}
                    {isSuperUser && <SelectItem value="integrator">Integrator</SelectItem>}
                    {currentUserLevel <= ROLE_HIERARCHY.admin && <SelectItem value="admin">Admin</SelectItem>}
                    {currentUserLevel <= ROLE_HIERARCHY.manager && <SelectItem value="manager">Manager</SelectItem>}
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-website">Website</Label>
              <Input
                id="edit-website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            {/* Active Status */}
            {canEditUsers && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="edit-active">User is active</Label>
              </div>
            )}

            {/* Invitation Actions (if user has invite token and not active) */}
            {editingUser && !editingUser.active && editingUser.invite_token && (
              <div className="border-t pt-4">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 space-y-3">
                  {isInviteExpired(editingUser) ? (
                    <p className="text-sm text-amber-800">
                      <span>This invitation expired on</span> {formatDate(editingUser.invite_expires)}<span>. Resend to issue a new link.</span>
                    </p>
                  ) : (
                    <p className="text-sm text-amber-800">
                      This user has not yet activated their account.
                    </p>
                  )}

                  {/* Copy Invite Link — hidden once expired (the link is dead). */}
                  {!isInviteExpired(editingUser) && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Invitation Link:</Label>
                      <div className="flex gap-2">
                        <Input
                          value={`${window.location.origin}/auth/set-password?token=${editingUser.invite_token}`}
                          readOnly
                          className="font-mono text-xs"
                          onClick={(e) => e.currentTarget.select()}
                        />
                        <Button type="button" variant="outline" size="sm" onClick={copyInviteLink}>
                          {isCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <span>⚠️ Share this link securely with the user. It will expire on</span> {formatDate(editingUser.invite_expires)}<span>.</span>
                      </p>
                    </div>
                  )}

                  {/* Resend Email - only if sending email is configured */}
                  {hasSendingEmail ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Send via Email</Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleResendInvite}
                        disabled={isResendingInvite}
                        className="w-full"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        <span>{isResendingInvite ? "Sending..." : "Send Invitation Email"}</span>
                      </Button>

                      {/* Email Send Status */}
                      {emailSendStatus && (
                        <Alert className={emailSendStatus.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                          <AlertDescription className={emailSendStatus.type === "success" ? "text-xs text-green-800" : "text-xs text-red-800"}>
                            {emailSendStatus.message}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <Alert className="border-blue-200 bg-blue-50">
                      <AlertDescription className="text-xs text-blue-800">
                        <span>To send invitation emails directly, please configure a Gmail account for sending on the</span>{" "}
                        <Link href="/?page=integrations" className="underline font-medium">
                          Integrations page
                        </Link>
                        <span>.</span>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            )}

            {editError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">
                  {editError}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Permissions Dialog (opened from a card's actions menu) */}
      <UserPermissionsDialog
        user={permissionsTarget}
        open={!!permissionsTarget}
        onOpenChange={(open) => { if (!open) setPermissionsTarget(null) }}
        onUpdatePermissions={handleUpdatePermissions}
      />

      {/* Make Owner Confirmation Dialog */}
      <AlertDialog
        open={!!makeOwnerTarget}
        onOpenChange={(open) => { if (!open) setMakeOwnerTarget(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Make tenant owner</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p><span>Set</span> <strong>{makeOwnerTarget?.name}</strong> <span>as the tenant owner?</span></p>
                <p className="text-gray-600 dark:text-gray-400">
                  The owner is the account&apos;s billing contact and has full control over the account — including permanently deleting the entire account and all of its data.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMakingOwner}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMakeOwner}
              disabled={isMakingOwner}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isMakingOwner ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key="saving">Saving...</span>
                </>
              ) : (
                <span key="make-owner">Make Owner</span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p><span>Are you sure you want to delete &quot;</span>{userToDelete?.name}<span>&quot;?</span></p>
                <p className="text-yellow-600 dark:text-yellow-500">
                  Records created by this user (feedback, agents, etc.) will lose their author reference.
                </p>
                <p className="font-medium">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key="deleting">Deleting...</span>
                </>
              ) : (
                <span key="delete">Delete</span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
